import { Edit } from "@mui/icons-material";
import { Box, Button, styled } from "@mui/material";
import BulkPrintButton from "../../../components/shared/BulkPrintButton";
import { useDateSearchParamOrFallbackToToday } from "../../../hooks/useDateSearchParamOrFallbackToToday";
import { useSelectedServiceCenter } from "../../../hooks/useSelectedServiceCenter";
import {
  Appointment,
  AppointmentOld,
  Route,
} from "../../../services/prePlanningService.types";
import { ContextMenuAction } from "../../../types/contextMenuAction";
import { InboundView } from "../../../types/inbound/inboundView.type";
import { Trap } from "../../../types/planning/trap.type";
import AddToPlanActionButton from "../prePlanning/actionbar/AddToPlanActionButton";
import AssignActionButton from "../prePlanning/actionbar/AssignActionButton";
import MergeActionButton from "../prePlanning/actionbar/MergeActionButton";

import { useIsRouteImmutable } from "../../../hooks/useIsRouteImmutable";
import type { PlanTableFormat } from "../../../types/planning/plan.type";
import { UnassignButton } from "../inboundPlanning/tables/UnassignButton";
import { SummaryContext } from "./ActionsMenuContext";
import CreateActionMenuButton from "./CreateActionMenuButton";
import DeleteButton from "./DeleteButton";

export interface InboundActionBarProps {
  activeTab: InboundView;
  selectedRowsContext: SummaryContext[];
  contextMenuActions: ContextMenuAction[];
  selectedBuckets?: Appointment[] | AppointmentOld[];
  selectedTraps?: Trap[];
  selectedRoutes?: Route[];
  routeOrTrap?: PlanTableFormat | null;
  clearSelection?: () => void;
  loadingTrapIds?: number[];
  setLoadingTrapIds?: (ids: number[]) => void;
  onEditReason?: () => void;
}

const SummaryVerticalBar = styled(Box)(({ theme }) => ({
  width: "0px",
  border: "1px solid",
  borderColor: theme.palette.divider,
  flex: "none",
  order: 0,
  flexGrow: 0,
}));

const InboundActionBar = ({
  activeTab,
  contextMenuActions,
  selectedRowsContext,
  selectedBuckets = [],
  selectedTraps = [],
  selectedRoutes = [],
  routeOrTrap = null,
  clearSelection = () => {},
  loadingTrapIds = [],
  setLoadingTrapIds,
  onEditReason,
}: InboundActionBarProps) => {
  const createActions = contextMenuActions.filter((a) =>
    a.label.startsWith("Create")
  );

  const assignActions = contextMenuActions.filter((a) =>
    a.label.includes(activeTab === "routes" ? "Assign To" : "Assign")
  );

  const mergeAction = contextMenuActions.find(
    (a) => a.label.toLowerCase() === `merge ${activeTab}`
  );

  const [serviceCenter] = useSelectedServiceCenter();
  const [selectedDate] = useDateSearchParamOrFallbackToToday();

  // disable shipment actions when tab is shipments and route is immutable
  const isRouteImmutable = useIsRouteImmutable(
    routeOrTrap?.status,
    routeOrTrap?.shipments
  );

  const disableShipmentActions = activeTab === "shipments" && isRouteImmutable;
  const excludedTabs = ["routes", "traps", "freightOnDock", "osad"];

  return (
    <Box display="flex" flexWrap="wrap" justifyContent="flex-end" gap="12px">
      {((activeTab === "traps" &&
        selectedTraps.filter((trap) => trap.bills > 0).length === 0) ||
        (activeTab === "routes" &&
          selectedRoutes.filter((route) => route.bills > 0).length === 0)) && (
        <DeleteButton
          activeTab={activeTab}
          selectedTraps={selectedTraps}
          selectedRoutes={selectedRoutes}
          clearSelection={clearSelection}
        />
      )}

      {!disableShipmentActions && !excludedTabs.includes(activeTab) && (
        <CreateActionMenuButton createActions={createActions} />
      )}

      {!disableShipmentActions && (
        <AssignActionButton assignActions={assignActions} />
      )}

      {(activeTab === "shipments" || activeTab === "osad") &&
        !disableShipmentActions && (
          <UnassignButton
            selectionContext={selectedRowsContext}
            clearSelection={clearSelection}
          />
        )}
      {activeTab === "osad" && onEditReason && (
        <Button
          variant="outlined"
          color="primary"
          startIcon={<Edit />}
          sx={{ textTransform: "none" }}
          onClick={() => onEditReason()}
        >
          Edit
        </Button>
      )}
      {(activeTab === "routes" || activeTab === "traps") &&
        mergeAction &&
        selectedRowsContext.length > 1 && (
          <MergeActionButton mergeAction={mergeAction} />
        )}
      {activeTab === "traps" && (
        <BulkPrintButton
          selectedTraps={selectedTraps}
          serviceCenterId={serviceCenter.id}
          loadingTrapIds={loadingTrapIds}
          planDate={selectedDate}
          setLoadingTrapIds={setLoadingTrapIds}
        />
      )}
      {(activeTab === "traps" || activeTab === "appointments") && (
        <>
          <SummaryVerticalBar />

          <AddToPlanActionButton
            selectedTrapOrBucket={
              activeTab === "traps" ? selectedTraps : selectedBuckets
            }
            selectedRowsContext={selectedRowsContext}
            clearSelection={clearSelection}
          />
        </>
      )}
    </Box>
  );
};

export default InboundActionBar;
